<template>
  <fieldset class="form-employee-work__weekdays">
    <legend hidden>Выберите дни недели</legend>
    <date-range-picker-dialog v-bind:value="initialDate" v-on:input="onInputDate">
      <template v-slot:trigger="{ listenerObject }">
        <p class="form-employee-work__days__date-title">Действует с:
          <button
            class="form-employee-work__days__data"
            type="button"
            v-on="listenerObject"
          >{{ dateTitle }}</button>
        </p>
      </template>
    </date-range-picker-dialog>
    <div
      class="form-employee-work__weekdays__day"
      v-for="{
        isChecked, title, value, workTimes,
      } in initialDays"
      v-bind:key="value"
    >
      <input-checkbox
        v-bind:title="title"
        v-bind:value="isChecked"
        v-on:input="onInputChecked(value, $event)"
      />
      <div class="form-employee-work__weekdays__periods" v-if="isChecked">
        <div
          class="form-employee-work__weekdays__row"
          v-for="(times, index) in workTimes"
          v-bind:key="times.key"
        >
          <time-interval-picker
            class="form-employee-work__weekdays__row t"
            v-bind:initial-from="times.from"
            v-bind:initial-to="times.to"
            v-bind:time-set="suggestedHours"
            v-on:input-from="onInputWorkTime(value, index, $event, times.to)"
            v-on:input-to="onInputWorkTime(value, index, times.from, $event)"
          />
          <button
            v-if="index === 0"
            class="form-employee-work__button-add"
            title="Добавить"
            type="button"
            v-bind:disabled="workTimes.length >= maxWorkIntervalsInDay"
            v-on:click="onAddWorkTimes(value)"
          />
          <button
            v-else
            class="form-employee-work__button-remove"
            title="Удалить"
            type="button"
            v-on:click="onRemoveWorkTimes(value, index)"
          />
        </div>
      </div>
    </div>
  </fieldset>
</template>
<script>
import DateRangePickerDialog from '@/components/DateRangePickerDialog.vue';
import InputCheckbox from '@/components/InputCheckbox.vue';
import TimeIntervalPicker from '@/components/TimeIntervalPicker.vue';

function WorkTimes(from, to, key) {
  this.from = from;
  this.to = to;
  this.key = key;

  this.setTimes = (newFrom, newTo) => new this.constructor(newFrom, newTo, key);
}

function WeekDay(isChecked, title, value, workTimes) {
  this.isChecked = isChecked;
  this.title = title;
  this.value = value;
  this.workTimes = workTimes;

  this.setIsChecked = (newValue) => new this.constructor(newValue, title, value, workTimes);
  this.setWorkTimes = (newValue) => new this.constructor(isChecked, title, value, newValue);
}

const weekdays = [
  {
    title: 'Пн',
    value: 'Monday',
  },
  {
    title: 'Вт',
    value: 'Tuesday',
  },
  {
    title: 'Ср',
    value: 'Wednesday',
  },
  {
    title: 'Чт',
    value: 'Thursday',
  },
  {
    title: 'Пт',
    value: 'Friday',
  },
  {
    title: 'Сб',
    value: 'Saturday',
  },
  {
    title: 'Вс',
    value: 'Sunday',
  },
];

/**
 * @description Получить дни недели.
 * @param {any[]}  initialDays
 * @param {string} from
 * @param {string} to
 * @returns {WeekDay[]}
 */
export function getWeekdays(initialDays, from, to) {
  const days = Object.fromEntries(
    initialDays.map(({ day, work_times: workTimes }) => [day, workTimes]),
  );
  return weekdays.map(({ title, value }) => new WeekDay(
    value in days,
    title,
    value,
    (days[value] || [{ starts_time: from, ends_time: to }])
      .map((times, index) => new WorkTimes(
        times.starts_time,
        times.ends_time,
        `${value}-${index}`,
      )),
  ));
}

/**
 * @description Получить будние дни по умолчанию.
 * @param {string} from
 * @param {string} to
 * @returns {WeekDay[]}
 */
export function getDefaultWeekdays(from = '00:00', to = '00:00') {
  const checked = weekdays.slice(0, 5).map(({ value }) => value);
  return weekdays.map(({ title, value }) => new WeekDay(
    checked.includes(value),
    title,
    value,
    [new WorkTimes(from, to, `${value}-initial`)],
  ));
}

/**
 * @description Компонент выбора дней недели.
 * @type {Vue}
 */
export default {
  components: {
    DateRangePickerDialog, InputCheckbox, TimeIntervalPicker,
  },
  computed: {
    dateTitle: (vm) => {
      const options = {
        day: 'numeric',
        month: 'long',
      };
      return vm.initialDate.toLocaleDateString('ru-RU', options);
    },
  },
  inject: ['maxWorkIntervalsInDay'],
  methods: {
    findDay(weekDay) {
      const dayIndex = this.initialDays.findIndex((candidate) => candidate.value === weekDay);
      const day = this.initialDays[dayIndex];
      return [dayIndex, day];
    },
    onAddWorkTimes(weekDay) {
      const [dayIndex, day] = this.findDay(weekDay);
      this.onChange(
        dayIndex,
        day.setWorkTimes([...day.workTimes,
          new WorkTimes(this.initialFrom, this.initialTo, Date.now()),
        ]),
      );
    },
    onChange(index, value) {
      this.$emit('change', index, value);
    },
    onInputChecked(weekDay, isChecked) {
      const [dayIndex, day] = this.findDay(weekDay);
      this.onChange(dayIndex, day.setIsChecked(isChecked));
    },
    onInputWorkTime(weekDay, index, from, to) {
      const [dayIndex, day] = this.findDay(weekDay);
      const workTimes = day.workTimes.map(
        (candidate, candidateIndex) => (
          candidateIndex === index
            ? candidate.setTimes(from, to)
            : candidate),
      );
      this.onChange(dayIndex, day.setWorkTimes(workTimes));
    },
    onInputDate(value) {
      this.$emit('input-date', value);
    },
    onRemoveWorkTimes(weekDay, index) {
      const [dayIndex, day] = this.findDay(weekDay);
      const workTimes = day.workTimes.filter((_, candidateIndex) => index !== candidateIndex);
      this.onChange(dayIndex, day.setWorkTimes(workTimes));
    },
  },
  name: 'WeekdaysPicker',
  props: {
    /**
     * @description Дни недели.
     */
    initialDays: { default: () => [], type: Array },
    /**
     * @description Предложенные часы работы.
     */
    suggestedHours: { default: () => [], type: Array },
    /**
     * @description Часы работы от.
     */
    initialFrom: { default: () => '00:00', type: String },
    /**
     * @description Часы работы до.
     */
    initialTo: { default: () => '00:00', type: String },
    /**
     * @description Дата.
     */
    initialDate: { default: () => new Date(), type: Date },
  },
};
</script>
<style scoped lang="scss">
@use "@/scss/buttons";

.form-employee-work {
  &__days {
    display: flex;
    align-items: center;
    flex-direction: row;
    &__date-title {
      flex-direction: row;
      width: 100%;
      font-size: 14px;
    }
    &__text {
      font-weight: 400;
      font-size: 14px;
      line-height: 14px;
    }
    &__data {
      color: var(--action-color);
      font-weight: 400;
      font-size: 14px;
      line-height: 14px;
    }
  }
  &__weekdays {
    border: none;
    display: grid;
    align-items: center;
    gap: 10px 20px;
    padding: 0 0 0 34px;
    margin: 5px 0 20px 0;
    &__day {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
      min-height: 40px;
    }
    &__periods {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    &__row {
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }
  &__button {
    &-add {
      text-align: left;
      color: #0489e0;
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      @include buttons.btn-plus-color-add;
    }
    &-remove {
      text-align: left;
      color: var(--warning-color);
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      @include buttons.btn-cross-color-remove;
    }
  }
}

@media screen and (max-width: 480px) {
  .form-employee-work {
    &__weekdays {
      padding-left: 0;
    }
  }
}
</style>
